import React, { useEffect, useRef, useState } from "react";
import QrScanner from "qr-scanner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showToast } from "../../redux/actions/toastActions.js";
import { useDispatch, useSelector } from "react-redux";
import { postAuthenticatedRequest } from "../../utils/API/api.ts";
import Tesseract from "tesseract.js";
import { useForm } from "react-hook-form";
import nlp from "compromise";
import CardDataModal from "./CardDataModal.jsx";
import { useQueryClient } from "@tanstack/react-query";
import winkNLP from "wink-nlp";
import model from "wink-eng-lite-web-model";

const nlpWink = winkNLP(model);
const its = nlpWink.its;

const ScanPopup = ({ camera, setIsOpen, setScannedUser, eventId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let videoElemRef = useRef();
  const qrScannerRef = useRef(null);
  const [scanMode, setScanMode] = useState("qr");
  const [loading, setLoading] = useState(false);
  // const userDetails = useSelector((state) => state.userDetails);
  // const { savedUserConfig } = userDetails;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const eventsId = searchParams.get("eventId");
  const [ocrProgress, setOcrProgress] = useState(0);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo: savedUserConfig } = userLogin || {};
  const { register, setValue, getValues, watch, reset } = useForm();

  const createLead = async (uidParam) => {
    await postAuthenticatedRequest(
      `${process.env.REACT_APP_SERVER_URL_LEADS}/attendee/${savedUserConfig?._id}/addScannedLeads?scannedUserId=${uidParam}&eventId=${eventId}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
      .then(function (response) {
        if (response.status === 200) {
          queryClient.invalidateQueries(["leadsCount", eventId]);
          queryClient.invalidateQueries(["userRanking", eventId]);
          queryClient.invalidateQueries(["leads", eventId]);
          setScannedUser({
            ...response?.data?.scannedData,
            isLeadSuccess: true,
            meetingTime: response?.data?.meetingTime,
          });
          // dispatch(
          //   showToast({
          //     message: "Lead Created Successfully",
          //     category: "success",
          //   })
          // );
          setIsOpen(true);
          qrScannerRef.current?.destroy();
          // navigate(`/attendee/${eventIdParam}?tab=leads&view=leads`);
        }
      })
      .catch(function (error) {
        if (
          error?.response?.data?.message === "Attendee already exists" &&
          error?.response?.status === 403
        ) {
          setScannedUser({
            ...error?.response?.data?.scannedData,
            isLeadSuccess: false,
            meetingTime: error?.response?.data?.meetingTime,
          });
          dispatch(
            showToast({
              message: "Lead already exists",
              category: "danger",
            })
          );

          setIsOpen(true);
          qrScannerRef.current?.destroy();
        } else if (
          error?.response?.data?.message === "Event or Attendee not found" &&
          error?.response?.status === 404
        ) {
          dispatch(
            showToast({
              message: "Invalid QR Code or Attendee",
              category: "danger",
            })
          );
          qrScannerRef.current?.destroy();
          navigate(-1);
        } else {
          dispatch(
            showToast({
              message: "Sorry, some error occurred",
              category: "danger",
            })
          );
          qrScannerRef.current?.destroy();
          navigate(-1);
        }

        qrScannerRef.current?.destroy();
        // navigate(-1);
      });
  };

  const preprocessCard = (file, maxWidth = 1024) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxWidth / img.width);
        const width = Math.floor(img.width * scale);
        const height = Math.floor(img.height * scale);

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        let imageData = ctx.getImageData(0, 0, width, height);
        let data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i],
            g = data[i + 1],
            b = data[i + 2];
          const gray = 0.299 * r + 0.587 * g + 0.114 * b;
          data[i] = data[i + 1] = data[i + 2] = gray;
        }

        const copy = new Uint8ClampedArray(data);
        const w = width;
        const h = height;
        const getGray = (x, y) => {
          if (x < 0) x = 0;
          if (x >= w) x = w - 1;
          if (y < 0) y = 0;
          if (y >= h) y = h - 1;
          return copy[4 * (y * w + x)];
        };

        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const neighbors = [
              getGray(x - 1, y - 1),
              getGray(x, y - 1),
              getGray(x + 1, y - 1),
              getGray(x - 1, y),
              getGray(x, y),
              getGray(x + 1, y),
              getGray(x - 1, y + 1),
              getGray(x, y + 1),
              getGray(x + 1, y + 1),
            ];
            neighbors.sort((a, b) => a - b);
            const median = neighbors[4];
            const idx = 4 * (y * w + x);
            data[idx] = data[idx + 1] = data[idx + 2] = median;
          }
        }

        const blockSize = 16;
        const integral = new Uint32Array(w * h);

        for (let y = 0; y < h; y++) {
          let rowSum = 0;
          for (let x = 0; x < w; x++) {
            const gray = data[4 * (y * w + x)];
            rowSum += gray;
            integral[y * w + x] =
              rowSum + (y > 0 ? integral[(y - 1) * w + x] : 0);
          }
        }

        const getBlockMean = (x1, y1, x2, y2) => {
          const A = x1 >= 0 && y1 >= 0 ? integral[y1 * w + x1] : 0;
          const B = x2 >= 0 && y1 >= 0 ? integral[y1 * w + x2] : 0;
          const C = x1 >= 0 && y2 >= 0 ? integral[y2 * w + x1] : 0;
          const D = x2 >= 0 && y2 >= 0 ? integral[y2 * w + x2] : 0;
          return (D - B - C + A) / ((x2 - x1 + 1) * (y2 - y1 + 1));
        };

        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const x1 = x - Math.floor(blockSize / 2);
            const y1 = y - Math.floor(blockSize / 2);
            const x2 = x + Math.floor(blockSize / 2);
            const y2 = y + Math.floor(blockSize / 2);
            const mean = getBlockMean(x1, y1, x2, y2);
            const idx = 4 * (y * w + x);
            const val = data[idx] > mean ? 255 : 0;
            data[idx] = data[idx + 1] = data[idx + 2] = val;
          }
        }

        const sharpenKernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];
        const copy2 = new Uint8ClampedArray(data);
        for (let y = 1; y < h - 1; y++) {
          for (let x = 1; x < w - 1; x++) {
            let sum = 0;
            for (let ky = -1; ky <= 1; ky++) {
              for (let kx = -1; kx <= 1; kx++) {
                const idx2 = 4 * ((y + ky) * w + (x + kx));
                const weight = sharpenKernel[(ky + 1) * 3 + (kx + 1)];
                sum += copy2[idx2] * weight;
              }
            }
            const idx = 4 * (y * w + x);
            const clamped = Math.min(255, Math.max(0, sum));
            data[idx] = data[idx + 1] = data[idx + 2] = clamped;
          }
        }

        ctx.putImageData(imageData, 0, 0);
        canvas.toBlob(resolve, "image/png");
      };

      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleNativeCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCapturedImage(URL.createObjectURL(file));
    setLoading(true);
    setOcrProgress(0);

    try {
      const preprocessedBlob = await preprocessCard(file);

      const {
        data: { text },
      } = await Tesseract.recognize(preprocessedBlob, "eng", {
        tessedit_char_whitelist:
          "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@.:/+& ",
        tessedit_pageseg_mode: Tesseract.PSM.SINGLE_BLOCK,
        logger: (m) => setOcrProgress(Math.round(m.progress * 100)),
      });

      if (!text.trim()) {
        dispatch(
          showToast({
            message: "No text detected on card.",
            category: "warning",
          })
        );
        setLoading(false);
        return;
      }

      const cleanText = text
        .replace(/[^A-Za-z0-9@.:/+&\s]/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();

      const lines = cleanText
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      const docComp = nlp(cleanText);
      const docWink = nlpWink.readDoc(cleanText);

      const phoneRegex = /\+?\d[\d .:/]{6,}\d/g;

      const phone =
        cleanText.match(phoneRegex)?.[0]?.replace(/[^\d+]/g, "") ||
        docComp.phoneNumbers().text() ||
        docWink
          .entities()
          .out(its.detail)
          .find((e) => e.type === "PHONE_NUMBER")?.value ||
        "";

      const emailRegex =
        /[A-Za-z0-9+]+(?:\s*\.\s*[A-Za-z0-9+]+)*\s*@\s*[A-Za-z0-9]+(?:\s*\.\s*[A-Za-z]{2,})+/;
      const websiteRegex =
        /\b(?:[A-Za-z0-9]+\s*\.\s*)+(?:com|net|org|io|co|in|edu|gov|biz|info|tech|ai|app|me|dev)(?:\s*\/[A-Za-z0-9+:/]*)?\b/gi;

      const email =
        docComp.emails().text() ||
        docWink
          .entities()
          .out(its.detail)
          .find((e) => e.type === "EMAIL")?.value ||
        cleanText.match(emailRegex)?.[0]?.replace(/\s+/g, "") ||
        "";

      let websites = (cleanText.match(websiteRegex) || [])
        .map((w) => w.replace(/\s+/g, "").replace(/[.,;!?]$/, ""))
        .filter((w) => !w.includes("@"));

      const website =
        docComp.urls().text() ||
        docWink
          .entities()
          .out(its.detail)
          .find((e) => e.type === "URL")?.value ||
        websites[0] ||
        "";

      let name = docComp.people().normalize().text() || "";
      if (!name)
        name =
          docComp.people().normalize().text() ||
          docWink
            .entities()
            .out(its.detail)
            .find((e) => e.type === "PERSON")?.value ||
          lines.find((l) => /^[A-Z][a-z]+(?: [A-Z][a-z]+)+$/.test(l)) ||
          "";

      const COMPANY_KEYWORDS = [
        "solutions",
        "technologies",
        "systems",
        "labs",
        "pvt",
        "inc",
        "ltd",
        "corp",
        "group",
        "enterprise",
      ];
      let companyName =
        docComp.organizations().text() ||
        docWink
          .entities()
          .out(its.detail)
          .find((e) => e.type === "ORG")?.value ||
        lines.find((line) =>
          COMPANY_KEYWORDS.some((k) => line.toLowerCase().includes(k))
        ) ||
        "";

      const ROLE_KEYWORDS = [
        "manager",
        "director",
        "ceo",
        "founder",
        "engineer",
        "developer",
        "designer",
        "consultant",
        "executive",
        "head",
        "lead",
        "specialist",
        "analyst",
        "coordinator",
      ];
      let role =
        lines.find((line) =>
          ROLE_KEYWORDS.some((k) => line.toLowerCase().includes(k))
        ) ||
        docWink
          .sentences()
          .out()
          .find((s) =>
            ROLE_KEYWORDS.some((k) => s.toLowerCase().includes(k))
          ) ||
        "";

      if (!role) {
        const jobPatterns = [
          "(Chief|Head|Lead|Senior|Principal)? [#Noun+] (Officer|Manager|Director|Engineer|Developer|Designer|Consultant|Executive|Analyst|Specialist|Architect|Scientist|Coordinator|Founder|President)",
          "(Founder|CEO|CTO|CFO|COO|President|Vice President|Managing Director)",
          "(Head|Director|Manager|Lead) of [#Noun+]",
        ];
        for (const pattern of jobPatterns) {
          const found = docComp.match(pattern).out("array");
          if (found.length) {
            role = found[0];
            break;
          }
        }
      }

      const address =
        docComp.places().out("text") ||
        docWink
          .entities()
          .out(its.detail)
          .find((e) => e.type === "GPE")?.value ||
        "";

      const compressedBlob = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const maxWidth = 1024;
          const scale = Math.min(1, maxWidth / img.width);
          canvas.width = Math.floor(img.width * scale);
          canvas.height = Math.floor(img.height * scale);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(resolve, "image/jpeg", 0.7);
        };
        img.src = URL.createObjectURL(file);
      });

      const imageData = URL.createObjectURL(compressedBlob);

      const parsed = {
        name,
        role,
        companyName,
        email,
        phone,
        website,
        address,
        imageData,
      };

      Object.keys(parsed).forEach((k) => {
        if (!parsed[k]) delete parsed[k];
      });

      Object.keys(parsed).forEach((key) => setValue(key, parsed[key]));
      setValue("cardModalOpen", true);
    } catch (err) {
      setCapturedImage(null);
      console.error("OCR Error:", err);
    } finally {
      setOcrProgress(0);
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   let accessToken = localStorage.getItem("accessToken");
  //   dispatch(getUserDetails({ accessToken: accessToken }));
  // }, [dispatch, savedUserConfig?._id]);

  useEffect(() => {
    if (!videoElemRef.current) return;
    qrScannerRef.current?.destroy();

    qrScannerRef.current = new QrScanner(
      videoElemRef.current,
      (result) => {
        let data = result.data;
        let uidParam = null;
        if (/^uid=[^&]+&eventId=[^&]+$/.test(data)) {
          uidParam = new URLSearchParams(data).get("uid");
        } else {
          let uid = data;
          if (data.startsWith("http")) {
            uid = new URL(data).searchParams.get("uid");
          } else if (data.startsWith("uid=")) {
            uid = new URLSearchParams(data).get("uid");
          } else if (data.includes(":")) {
            uid = data.split(":")[0].trim();
          }

          if (uid) {
            uidParam = uid;
          }
        }

        if (uidParam) {
          createLead(uidParam);

          qrScannerRef.current.stop();
          // navigate(-1);
        } else {
          console.log("nothing");
        }
      },
      {
        onDecodeError: (error) => {
          console.log(error);
        },
        highlightScanRegion: false,
        highlightCodeOutline: true,
        preferredCamera: camera,
        maxScansPerSecond: 10,
        calculateScanRegion: (video) => {
          // fixed 300x300px scan box centered in the video
          const boxWidth = 1000;
          const boxHeight = 750;
          return {
            x: (video.videoWidth - boxWidth) / 2,
            y: (video.videoHeight - boxHeight) / 2,
            width: boxWidth,
            height: boxHeight,
          };
        },
      }
    );

    qrScannerRef.current.start();

    return () => {
      qrScannerRef.current.stop();
    };
  }, [scanMode]);

  useEffect(() => {
    function preventBackgroundScroll(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    document.body.style.overflow = "hidden";
    document.addEventListener("scroll", preventBackgroundScroll, {
      passive: false,
    });

    return () => {
      document.body.style.overflow = "visible";
      document.removeEventListener("scroll", preventBackgroundScroll);
    };
  }, []);

  const handleClose = () => {
    qrScannerRef.current?.destroy();
    navigate(`/attendee/${eventsId}?tab=about`);
  };

  const changeCamera = async (id) => {
    navigate(-1);

    setTimeout(() => {
      navigate(
        `/connections/scan?camera=${
          camera === "environment" ? "user" : "environment"
        }`
      );
    }, 500);
  };

  return (
    <>
      <div className="pt-0 md:pt-0 md:mb-0 w-full md:w-full h-[100%] relative mymd:flex">
        <div className="w-full grid place-items-center rounded-[12px] mt-[100px] md:mt-[90px] px-5 md:px-0">
          {scanMode === "card" ? (
            <div
              className={`rounded-[12px] relative flex items-center justify-center w-[320px] h-[320px] mymd:w-[calc(100vw_-_550px)] mymd:h-[calc(100vh_-_130px)] max-w-1/2 max-h-full mymd:rounded-[15px] border mx-auto`}
              onClick={() => document.getElementById("cameraInput").click()}
            >
              {capturedImage ? (
                <img
                  src={capturedImage}
                  alt="Captured Card"
                  className="max-w-[80%] max-h-[80%] object-contain rounded-[12px]"
                />
              ) : (
                <svg
                  viewBox="0 0 80 80"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="max-w-[80%] max-h-[80%] object-contain"
                >
                  <rect
                    x="0"
                    y="0"
                    width="80"
                    height="80"
                    rx="12"
                    fill="#E5E7EB"
                  />
                  <path
                    d="M20 50 L40 30 L60 50"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle cx="30" cy="30" r="5" fill="#9CA3AF" />
                </svg>
              )}
            </div>
          ) : (
            <video
              id="videoElem"
              ref={videoElemRef}
              className={`rounded-[12px] relative mymd:w-[calc(100vw_-_400px)] w-[320px] h-[320px] mymd:h-[calc(100vh_-_130px)] object-fill mymd:rounded-[15px] border`}
            ></video>
          )}
        </div>

        <div className="fixed top-[0px] left-0 right-0 flex justify-around items-center w-full text-white mymd:rounded-[15px] mymd:w-[calc(100vw_-_520px)] font-[500] text-[16px] bg-white cursor-pointer py-3 px-5 gap-0">
          <div
            className={`${
              scanMode === "qr"
                ? "bg-primary text-white"
                : "bg-white text-[#79797a]"
            } h-full w-[100%] rounded-[10px] text-center py-3 flex items-center gap-2 justify-center`}
            onClick={() => {
              setScanMode("qr");
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="23"
              fill={`${scanMode === "qr" ? "#fff" : "#79797a"}  `}
              viewBox="0 -960 960 960"
              width="23"
            >
              <path d="M109.825-707Q97-707 88.5-715.625T80-737v-113q0-12.75 8.625-21.375T110-880h113q12.75 0 21.375 8.675 8.625 8.676 8.625 21.5 0 12.825-8.625 21.325T223-820h-83v83q0 12.75-8.675 21.375-8.676 8.625-21.5 8.625ZM110-80q-12.75 0-21.375-8.625T80-110v-113q0-12.75 8.675-21.375 8.676-8.625 21.5-8.625 12.825 0 21.325 8.625T140-223v83h83q12.75 0 21.375 8.675 8.625 8.676 8.625 21.5Q253-97 244.375-88.5T223-80H110Zm627 0q-12.75 0-21.375-8.675-8.625-8.676-8.625-21.5 0-12.825 8.625-21.325T737-140h83v-83q0-12.75 8.675-21.375 8.676-8.625 21.5-8.625 12.825 0 21.325 8.625T880-223v113q0 12.75-8.625 21.375T850-80H737Zm112.825-627Q837-707 828.5-715.625T820-737v-83h-83q-12.75 0-21.375-8.675-8.625-8.676-8.625-21.5 0-12.825 8.625-21.325T737-880h113q12.75 0 21.375 8.625T880-850v113q0 12.75-8.675 21.375-8.676 8.625-21.5 8.625ZM708-251h63v63h-63v-63Zm0-126h63v63h-63v-63Zm-63 63h63v63h-63v-63Zm-63 63h63v63h-63v-63Zm-63-63h63v63h-63v-63Zm126-126h63v63h-63v-63Zm-63 63h63v63h-63v-63Zm-63-63h63v63h-63v-63Zm252-332v252H519v-252h252ZM440-440v252H188v-252h252Zm0-332v252H188v-252h252Zm-50 534v-152H238v152h152Zm0-332v-152H238v152h152Zm331 0v-152H569v152h152Z" />
            </svg>
            QR Scanner
          </div>
          {/* <p
            className={`${
              scanMode === "card"
                ? "bg-primary text-white"
                : "bg-white text-[#79797a]"
            }  h-full w-[50%] rounded-[10px] text-center py-3 flex items-center gap-2 justify-center`}
            onClick={() => {
              setScanMode("card");
            }}
          >
            <svg
              fill={`${scanMode === "card" ? "#fff" : "#79797a"}  `}
              width="22px"
              height="22px"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
              <g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></g>
              <g id="SVGRepo_iconCarrier">
                {" "}
                <title></title>
                <g id="card">
                  <path d="M20.5,5H3.5A1.5,1.5,0,0,0,2,6.5v11A1.5,1.5,0,0,0,3.5,19h17A1.5,1.5,0,0,0,22,17.5V6.5A1.5,1.5,0,0,0,20.5,5ZM3.5,6h17a.5.5,0,0,1,.5.5V7.94H3V6.5A.5.5,0,0,1,3.5,6Zm17,12H3.5a.5.5,0,0,1-.5-.5V8.94H21V17.5A.5.5,0,0,1,20.5,18Z"></path>
                  <path d="M16.5,11a2.43,2.43,0,0,0-1.5.51A2.47,2.47,0,0,0,13.5,11a2.5,2.5,0,0,0,0,5,2.47,2.47,0,0,0,1.5-.51,2.43,2.43,0,0,0,1.5.51,2.5,2.5,0,0,0,0-5ZM12,13.5A1.5,1.5,0,1,1,13.5,15,1.5,1.5,0,0,1,12,13.5ZM16.5,15a1.5,1.5,0,0,1-.84-.27,2.39,2.39,0,0,0,0-2.46A1.5,1.5,0,1,1,16.5,15Z"></path>
                  <path d="M8.5,13h-3a.5.5,0,0,0,0,1h3a.5.5,0,0,0,0-1Z"></path>
                </g>
              </g>
            </svg>
            Card Scanner
          </p> */}
        </div>

        <div className="flex flex-col justify-start items-center md:hidden space-y-4 mt-3">
          {scanMode === "card" && (
            <div className="flex flex-col items-center w-full">
              <button
                onClick={() => document.getElementById("cameraInput").click()}
                disabled={loading}
                className={`${
                  loading
                    ? "bg-primary/70 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90 active:bg-primary/95"
                } text-white font-medium py-2.5 px-6 mt-2 rounded-[10px] text-center flex items-center justify-center gap-2 shadow-md transition-all duration-300`}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                      ></path>
                    </svg>
                    <span className="ml-1">
                      {ocrProgress > 0 && ocrProgress < 100
                        ? `Scanning Card... ${ocrProgress}%`
                        : "Extracting Details..."}
                    </span>
                  </>
                ) : (
                  "Capture Card"
                )}
              </button>
            </div>
          )}

          <p
            className="text-center mt-7 text-black md:hidden flex items-center gap-1.5"
            onClick={() => handleClose()}
          >
            <img
              src="/svgs/Arrowleft.svg"
              className="w-[23px] h-[23px] object-cover cursor-pointer"
              alt="back"
            />
            <span className="font-medium">Go Back</span>
          </p>
        </div>

        {/* <div className="mt-[50px] text-center rounded-[12px] mx-4 p-2 backdrop-blur-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20 shadow-lg animate-slide-up delay-200">
          <p className="text-[25px] font-medium">Align QR Code in Frame</p>
          <p className="mt-0.5 text-[15px] text-gray-600">
            Position the visitor's badge QR code within the scanning area for
            instant capture
          </p>
        </div> */}

        <div className="mt-[50px] text-center rounded-[12px] mx-4">
          <p className="text-[25px] font-medium">
            {scanMode === "card"
              ? "Align Business Card in Frame"
              : "Align QR Code in Frame"}
          </p>
          <p className="mt-0.5 text-[15px] text-gray-600">
            {scanMode === "card"
              ? "Position the business card within the scanning area for capture"
              : "Position the visitor's badge QR code within the scanning area for instant capture"}
          </p>
        </div>
      </div>

      <CardDataModal
        cardData={getValues()}
        onClose={() => {
          reset();
          setValue("cardModalOpen", false);
          setCapturedImage(null);
        }}
        triggerModal={watch("cardModalOpen")}
        register={register}
        watch={watch}
        getValues={getValues}
        setValue={setValue}
        attendeeId={savedUserConfig?._id}
        eventsId={eventsId}
        setLoading={setLoading}
        reset={reset}
      />

      <div className="fixed top-[10px] right-[17px] items-center gap-4 hidden md:flex">
        <div
          className="flex justify-center items-center w-[30px] h-[30px] rounded-full bg-gray-500 cursor-pointer"
          onClick={handleClose}
        >
          <img src="/svgs/Cross.svg" alt="cross" className="w-[22px]" />
        </div>
        <div
          className=" flex justify-center items-center w-[30px] h-[30px] rounded-full bg-gray-500 cursor-pointer"
          onClick={changeCamera}
        >
          <img
            src="/svgs/switch-camera.svg"
            alt="camera"
            className="w-[18px]"
            title="Switch Camera"
          />
        </div>
      </div>

      <input
        type="file"
        accept="image/*"
        capture="environment"
        id="cameraInput"
        style={{ display: "none" }}
        onChange={handleNativeCapture}
      />

      {/* <div className="w-full flex justify-start items-center border-b-2 outline-offset-8 pb-3 relative mymd:mb-[20px]">
        <span className="text-[16px] font-[500]">Genrate Leads</span>
        <img
          src="/svgs/switch-camera.svg"
          alt="camera"
          className="w-[25px] cursor-pointer ml-3"
          onClick={changeCamera}
          title="Switch Camera"
        />

        <span
          className="absolute right-0 rounded-full bg-gray-200 cursor-pointer w-[28px] h-[28px] hover:bg-gray-300"
          onClick={handleClose}
        >
          <div className="flex justify-center items-center text-gray-800 w-[28px] h-[28px] text-[17px] font-[500] hover:font-[600]">
            x
          </div>
        </span>
      </div>

      <div className="pt-0 md:pt-0 md:mb-0 mx-auto grid place-items-center  w-full md:max-w-[1440px] md:w-full mb-14 h-[300px]">
        <video
          id="videoElem"
          ref={videoElemRef}
          className={`rounded-[15px] relative top-[10px] md:top-[10px] md:w-full md:h-full w-full border-[4px] border-[#d5d5d5] mx-auto`}
        ></video>
      </div> */}
    </>
  );
};

export default ScanPopup;
